import type { Event, EventReminder } from 'models';
import { useMemo } from 'react';
import { Form, FormActions, FormLayout, Modal, message } from 'ui';
import {
  AttachmentsFormContent,
  type AttachmentsFormContentValues,
  DescriptionImageFormContent,
  type ExtendedDescriptionImageFormContentValues,
  type ExtendedInvitationsDuplicateFormValues,
  type ExtendedReminderDetailFormContentValues,
  type ExtendedReminderDurationFormValues,
  InvitationsDuplicateFormContent,
  ReminderDetailFormContent,
  ReminderDurationFormContent,
} from '../../components';
import { useUploadEventAttachments, useUploadEventCoverPhoto } from '../../hooks';
import { useDuplicateEventMutation } from '../../queries';
import { useCreateFormRequestData, useGetInitialValues } from './hooks';

export type DuplicateReminderFormValues = AttachmentsFormContentValues &
  ExtendedDescriptionImageFormContentValues &
  ExtendedInvitationsDuplicateFormValues &
  ExtendedReminderDetailFormContentValues &
  ExtendedReminderDurationFormValues;

type P = {
  event: EventReminder;
  onCancel: () => void;
  onSuccess: (eventData: Event) => void;
};

const DuplicateReminderForm = ({ event, onCancel, onSuccess }: P) => {
  const { isPending, mutate: duplicateEvent } = useDuplicateEventMutation();
  const { isUploadingAttachments, uploadEventAttachments } = useUploadEventAttachments();
  const { isUploadingCoverPhoto, uploadEventCoverPhoto } = useUploadEventCoverPhoto();
  const initialValues = useGetInitialValues(event);
  const createFormRequestData = useCreateFormRequestData();

  const onSubmit = async (formValues: DuplicateReminderFormValues) => {
    await uploadEventCoverPhoto({ eventId: event.id, heroImage: formValues.heroImage });
    await uploadEventAttachments({ attachments: formValues.attachments, eventId: event.id });
    duplicateEvent(createFormRequestData(formValues, event), {
      onSuccess(eventData) {
        message.success('Reminder duplicated successfully.');
        onSuccess(eventData);
      },
    });
  };

  const isLoading = useMemo(
    () => isPending || isUploadingAttachments || isUploadingCoverPhoto,
    [isPending, isUploadingAttachments, isUploadingCoverPhoto],
  );

  return (
    <Form<DuplicateReminderFormValues> initialValues={initialValues} onFinish={onSubmit}>
      <FormLayout>
        <ReminderDetailFormContent<DuplicateReminderFormValues> />
        <ReminderDurationFormContent<DuplicateReminderFormValues> />
        <InvitationsDuplicateFormContent<DuplicateReminderFormValues>
          label="Duplicate this reminder with everyone added?"
          noDescription="The duplicated reminder won't include any people."
          title="People"
          yesDescription="Everyone currently added will get the duplicated reminder."
        />
        <DescriptionImageFormContent<DuplicateReminderFormValues> />
        <AttachmentsFormContent<DuplicateReminderFormValues> />
      </FormLayout>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            onClick: onCancel,
          }}
          submit={{
            children: 'Duplicate',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default DuplicateReminderForm;
